import { computed, type ComputedRef } from 'vue'
import { useRouter } from 'vue-router'

import { useChatDock } from '@/components/chat/AppChatPanel/useChatDock'
import type { PieceAction } from '@/lib/content/piece-actions'
import { articlePageRoute } from '@/router/page-route'
import { postRoute } from '@/router/post-route'
import { useContentStore } from '@/stores/content'
import { usePostPanelStore, type PostAction } from '@/stores/post-panel'
import { isLoaded } from '@/types/load-state'

export interface PostPanel {
  /** The id of the post the dock shows, or `null` when the panel is closed. */
  openPostId: ComputedRef<string | null>
  /** Opens a post in the dock as the sibling sheet; the sidebar collapses. */
  openPost: (id: string) => void
  /** Closes the post panel and the dock with it. */
  closePost: () => void
  /** Closes the panel and goes to the full write surface: the article workbench or the post page. */
  openFull: (id: string) => void
  /** False only when the LinkedIn account is known to be missing or broken. */
  linkedInConnected: ComputedRef<boolean>
  isPending: (action: PostAction, id: string) => boolean
  schedule: (id: string) => Promise<boolean>
  unschedule: (id: string) => Promise<boolean>
  retryPublish: (id: string) => Promise<boolean>
  /** Moves a post's date: a booked post is rescheduled, a draft only gets the new date. */
  reschedule: (id: string, publishAt: string) => Promise<void>
  publishNow: (id: string) => Promise<void>
  remove: (id: string) => Promise<void>
  /** Runs one of a piece's actions as the dock runs it; reschedule opens the dock on its date control. */
  run: (action: PieceAction, id: string) => void
}

/** Opens any post in the dock's post panel from any page, and runs the post actions it offers. */
export function usePostPanel(): PostPanel {
  const store = usePostPanelStore()
  const content = useContentStore()
  const dock = useChatDock()
  const router = useRouter()

  function openPost(id: string): void {
    store.open(id)
    dock.showPost()
  }

  function closePost(): void {
    store.close()
    if (dock.panel.value === 'post') dock.closeDock()
  }

  function openFull(id: string): void {
    const shape = isLoaded(content.posts)
      ? content.posts.value.find((entry) => entry.id === id)?.shape
      : undefined
    store.close()
    void router.push(
      shape === 'article'
        ? (articlePageRoute(id, isLoaded(content.articles) ? content.articles.value : []) ?? {
            name: 'content-item',
            params: { id },
          })
        : postRoute(id),
    )
  }

  async function duplicate(id: string): Promise<void> {
    if (await store.duplicate(id)) dock.showPost()
  }

  const RUN: Record<PieceAction, (id: string) => void> = {
    open: openFull,
    edit: openFull,
    schedule: (id) => void store.schedule(id),
    publishNow: (id) => void store.publishNow(id),
    reschedule: (id) => {
      store.openAtDate(id)
      dock.showPost()
    },
    unschedule: (id) => void store.unschedule(id),
    retryPublish: (id) => void store.retryPublish(id),
    duplicate: (id) => void duplicate(id),
    remove: (id) => void store.remove(id),
  }

  return {
    openPostId: computed(() => store.postId),
    openPost,
    closePost,
    openFull,
    linkedInConnected: computed(() => store.linkedInConnected),
    isPending: store.isPending,
    schedule: store.schedule,
    unschedule: store.unschedule,
    retryPublish: store.retryPublish,
    reschedule: store.reschedule,
    publishNow: store.publishNow,
    remove: store.remove,
    run: (action, id) => RUN[action](id),
  }
}
